import { useState } from 'react';
import { ChevronDown, History } from 'lucide-react';
import { useHistoricoJogosDia } from '../../hooks/useHistoricoJogosDia';
import { BolaoResultadoHistorico } from './BolaoResultadoHistorico';
import {
  EstatisticasCard,
  IncidentesTimeline,
  EscalacaoCard,
  FunFactsCard,
} from './JogoSharedCards';
import MobileCraqueBagre from '../MobileCraqueBagre';
import type { FutebolPartidaAtiva, FutebolJogoDia, FutebolPartidaHistoricoItem } from '@/shared/types';

function horarioBRT(iso: string): string {
  return new Date(iso).toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'America/Sao_Paulo',
  });
}

function Escudo({ src, nome }: { src: string | null; nome: string }) {
  const [erro, setErro] = useState(false);
  if (!src || erro) {
    return (
      <span className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-[9px] font-bold text-white/50 shrink-0">
        {nome.slice(0, 2).toUpperCase()}
      </span>
    );
  }
  return (
    <img src={src} alt={nome} className="w-8 h-8 object-contain shrink-0" onError={() => setErro(true)} />
  );
}

function HistoricoItemCard({
  item,
  telaId,
  exibirBolao,
  expandido,
  onToggle,
}: {
  item: FutebolPartidaHistoricoItem;
  telaId: string;
  exibirBolao: boolean;
  expandido: boolean;
  onToggle: () => void;
}) {
  const nomeCasa = item.time_casa;
  const nomeVis  = item.time_visitante;
  const temDetalhes = Boolean(
    exibirBolao ||
    (item.incidents && item.incidents.length > 0) ||
    item.stats ||
    item.lineup?.status !== 'unavailable' ||
    (item.funfacts && item.funfacts.length > 0) ||
    item.ultimo_evento,
  );

  return (
    <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] overflow-hidden">
      <div className="px-4 py-3.5">
        {item.liga_nome && (
          <p className="mb-1 truncate text-[10px] font-medium text-white/35">
            {item.liga_nome}
          </p>
        )}
        <div className="flex items-center gap-2">
          <Escudo src={item.escudo_casa} nome={nomeCasa} />
          <div className="flex-1 min-w-0 text-center">
            <p className="text-[11px] text-white/70 truncate">{nomeCasa}</p>
            <p className="text-white font-black text-lg tabular-nums leading-tight my-0.5">
              {item.placar_casa} <span className="text-white/25 text-sm">×</span> {item.placar_visitante}
            </p>
            <p className="text-[11px] text-white/70 truncate">{nomeVis}</p>
          </div>
          <Escudo src={item.escudo_visitante} nome={nomeVis} />
        </div>
        <p className="text-[10px] text-white/35 text-center mt-2 font-mono">{horarioBRT(item.horario)}</p>
      </div>

      {temDetalhes && (
        <>
          <button
            type="button"
            onClick={onToggle}
            className="w-full px-4 py-2.5 flex items-center justify-between border-t border-white/[0.05] text-left active:bg-white/[0.03] transition-colors"
            aria-expanded={expandido}
          >
            <span className="text-[11px] font-medium text-white/40">Detalhes</span>
            <ChevronDown
              size={16}
              className={`text-white/30 transition-transform ${expandido ? 'rotate-180' : ''}`}
            />
          </button>

          {expandido && (
            <div className="px-4 pb-4 space-y-3 border-t border-white/[0.05] pt-3">
              {exibirBolao && (
                <BolaoResultadoHistorico
                  telaId={telaId}
                  fixtureId={item.fixture_id}
                  placarCasa={item.placar_casa}
                  placarVisitante={item.placar_visitante}
                />
              )}

              {item.incidents && item.incidents.length > 0 && (
                <IncidentesTimeline
                  incidents={item.incidents}
                  nomeCasa={nomeCasa}
                  nomeVisitante={nomeVis}
                />
              )}

              {item.stats && (
                <EstatisticasCard stats={item.stats} nomeCasa={nomeCasa} nomeVisitante={nomeVis} />
              )}

              <MobileCraqueBagre
                telaId={telaId}
                fixtureId={item.fixture_id}
                ultimoEvento={item.ultimo_evento ?? null}
              />

              {item.lineup && item.lineup.status !== 'unavailable' && (
                <EscalacaoCard lineup={item.lineup} nomeCasa={nomeCasa} nomeVisitante={nomeVis} />
              )}

              {item.funfacts && item.funfacts.length > 0 && (
                <FunFactsCard funfacts={item.funfacts} />
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

interface Props {
  telaId: string;
  partidaAtual: FutebolPartidaAtiva | null;
  jogoAtual: FutebolJogoDia | null;
  exibirBolao: boolean;
  /** Evita duplicar o jogo já exibido na view principal (ex.: encerrado). */
  excludeFixtureId?: number | null;
}

export function JogoHistoricoDia({ telaId, partidaAtual, jogoAtual, exibirBolao, excludeFixtureId = null }: Props) {
  const { itens, loading } = useHistoricoJogosDia(telaId, partidaAtual, jogoAtual);
  const [expandidoId, setExpandidoId] = useState<string | null>(null);

  const visiveis = excludeFixtureId != null
    ? itens.filter((i) => i.fixture_id !== excludeFixtureId)
    : itens;

  if (loading || visiveis.length === 0) return null;

  return (
    <section className="space-y-3 px-4 pb-24 pt-2">
      <div className="flex items-center gap-2 px-1">
        <History size={14} className="shrink-0 text-white/35" />
        <h2 className="text-[11px] font-medium text-white/40">
          Transmitidos hoje
        </h2>
        <span className="ml-auto font-mono text-[10px] tabular-nums text-white/30">{visiveis.length}</span>
      </div>

      {visiveis.map((item) => (
        <HistoricoItemCard
          key={item.id}
          item={item}
          telaId={telaId}
          exibirBolao={exibirBolao}
          expandido={expandidoId === item.id}
          onToggle={() => setExpandidoId((prev) => (prev === item.id ? null : item.id))}
        />
      ))}
    </section>
  );
}
